import React, { useState, useEffect } from 'react';
import DataTable from '../Tables/DataTable';
import { Booking, BookingStatus, PaymentStatus } from '../../types/booking';
import { bookingService } from '../../services/bookingService';

interface BookingListingProps {
  bookings: Booking[];
  onBookingSelect?: (booking: Booking) => void;
  onBookingUpdate?: (booking: Booking) => void;
  onInvoiceView?: (booking: Booking) => void;
  loading?: boolean;
  highlightedBookingId?: string | null;
}

const BookingListing: React.FC<BookingListingProps> = ({
  bookings,
  onBookingSelect,
  onBookingUpdate,
  onInvoiceView,
  loading = false,
  highlightedBookingId
}) => {
  const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleBookingSelection = (bookingId: string, selected: boolean) => {
    if (selected) {
      setSelectedBookingIds(prev => [...prev, bookingId]);
    } else {
      setSelectedBookingIds(prev => prev.filter(id => id !== bookingId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedBookingIds(bookings.map(b => b._id));
    } else {
      setSelectedBookingIds([]);
    }
  };

  const handleStatusUpdate = async (bookingId: string, status: BookingStatus) => {
    setActionLoading(bookingId);
    try {
      const response = await bookingService.updateBookingStatus(bookingId, {
        status,
        modifiedBy: 'admin' // This should come from auth context
      });
      
      if (response.success && response.data && onBookingUpdate) {
        onBookingUpdate(response.data);
      }
    } catch (error) {
      console.error('Failed to update booking status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: BookingStatus) => {
    const colorClass = bookingService.getStatusColor(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {bookingService.formatStatus(status)}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const colorClass = bookingService.getPaymentStatusColor(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {bookingService.formatStatus(status)}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date: Date | string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isNewBooking = (date: Date | string) => {
    const createdAt = new Date(date).getTime();
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return now - createdAt <= twentyFourHours;
  };

  const columns = [
    {
      key: 'select',
      title: 'Select',
      render: (_: any, record: Booking) => (
        <input
          type="checkbox"
          checked={selectedBookingIds.includes(record._id)}
          onChange={(e) => handleBookingSelection(record._id, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      )
    },
    {
      key: 'bookingReference',
      title: 'Reference',
      sortable: true,
      render: (reference: string, record: Booking) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{reference}</span>
            {isNewBooking(record.createdAt) && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200" title="New booking in last 24 hours">
                New
              </span>
            )}
            {/* Source badge - check both source field and isManual for backwards compatibility */}
            {((record as any).source === 'manual' || (record as any).source === 'dashboard' || (record as any).isManual) ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" title="Manual/Dashboard Booking">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Manual
              </span>
            ) : (record as any).source === 'website' || !(record as any).source ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Website Booking">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                </svg>
                Website
              </span>
            ) : null}
          </div>
          <div className="text-sm text-gray-500">
            {formatDateTime(record.createdAt)}
          </div>
        </div>
      )
    },
    {
      key: 'customer',
      title: 'Customer',
      sortable: true,
      render: (customer: any) => (
        <div>
          {customer ? (
            <>
              <div className="font-medium text-gray-900">
                {customer.firstName || ''} {customer.lastName || ''}
              </div>
              <div className="text-sm text-gray-500">{customer.email || 'N/A'}</div>
              {(customer.contact || customer.phone) && (
                <div className="text-sm text-gray-500">{customer.contact || customer.phone}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-500 italic">Customer not found</div>
          )}
        </div>
      )
    },
    {
      key: 'car',
      title: 'Vehicle',
      sortable: true,
      render: (car: any) => (
        <div>
          {car ? (
            <>
              <div className="font-medium text-gray-900">{car.name || 'N/A'}</div>
              <div className="text-sm text-gray-500">
                {car.brand || ''} {car.model || ''} {car.year ? `(${car.year})` : ''}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 italic">Vehicle not found</div>
          )}
        </div>
      )
    },
    {
      key: 'pickUpDate',
      title: 'Pickup',
      sortable: true,
      render: (pickUpDate: Date, record: Booking) => (
        <div>
          <div className="font-medium text-gray-900">
            {formatDate(pickUpDate)}
          </div>
          <div className="text-sm text-gray-500">
            {record.pickUpTime} - {record.pickUpLocation}
          </div>
        </div>
      )
    },
    {
      key: 'returnDate',
      title: 'Return',
      sortable: true,
      render: (returnDate: Date, record: Booking) => (
        <div>
          <div className="font-medium text-gray-900">
            {formatDate(returnDate)}
          </div>
          <div className="text-sm text-gray-500">
            {record.returnTime} - {record.returnLocation}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (status: BookingStatus, record: Booking) => (
        <div className="space-y-1">
          {getStatusBadge(status)}
          {record.hasConflict && (
            <div className="text-xs text-red-600 font-medium">⚠️ Conflict</div>
          )}
          {record.requiresApproval && (
            <div className="text-xs text-purple-600 font-medium">👤 Needs Approval</div>
          )}
          {bookingService.isBookingOverdue(record) && (
            <div className="text-xs text-orange-600 font-medium">⏰ Overdue</div>
          )}
        </div>
      )
    },
    {
      key: 'paymentStatus',
      title: 'Payment',
      sortable: true,
      render: (paymentStatus: PaymentStatus, record: Booking) => (
        <div>
          {getPaymentStatusBadge(paymentStatus)}
          <div className="text-sm text-gray-500 mt-1">
            {formatCurrency(record.totalAmount)}
          </div>
          {record.paidAmount > 0 && (
            <div className="text-xs text-green-600">
              Paid: {formatCurrency(record.paidAmount)}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'duration',
      title: 'Duration',
      render: (_: any, record: Booking) => {
        const duration = bookingService.calculateBookingDuration(
          new Date(record.pickUpDate),
          new Date(record.returnDate)
        );
        return (
          <div className="text-sm text-gray-900">
            {duration} day{duration !== 1 ? 's' : ''}
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: Booking) => (
        <div className="flex flex-col space-y-2">
          <div className="flex space-x-2">
            <button
              onClick={() => onBookingSelect?.(record)}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="View & Edit Booking"
            >
              View/Edit
            </button>
            <button
              onClick={() => onInvoiceView?.(record)}
              className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              title="View Invoice"
            >
              Invoice
            </button>
          </div>
          <select
            value={record.status}
            onChange={(e) => handleStatusUpdate(record._id, e.target.value as BookingStatus)}
            disabled={actionLoading === record._id}
            className="text-xs border border-gray-300 rounded px-2 py-1 min-w-[100px]"
          >
            {Object.values(BookingStatus).map(status => (
              <option key={status} value={status}>
                {bookingService.formatStatus(status)}
              </option>
            ))}
          </select>
        </div>
      )
    }
  ];

  if (loading && bookings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading bookings...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow">
      {/* Header with bulk actions */}
      {selectedBookingIds.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedBookingIds.length} booking{selectedBookingIds.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedBookingIds([])}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Selection
              </button>
              {/* Add bulk actions here if needed */}
            </div>
          </div>
        </div>
      )}

      {/* Table Header with Select All */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">
              Bookings ({bookings.length})
            </h3>
            <input
              type="checkbox"
              checked={selectedBookingIds.length === bookings.length && bookings.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-500">Select All</span>
          </div>
          
          <div className="relative">
            <input
              type="text"
              placeholder="Search bookings..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bookings.map((booking) => {
              const isHighlighted = highlightedBookingId === booking._id;
              return (
                <tr 
                  key={booking._id} 
                  id={`booking-row-${booking._id}`}
                  className={`transition-all duration-500 ${
                    isHighlighted 
                      ? 'bg-blue-100 ring-2 ring-blue-500 ring-inset shadow-lg' 
                      : 'hover:bg-gray-50'
                  }`}
                  style={isHighlighted ? { animation: 'pulse 2s ease-in-out 3' } : {}}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.render 
                        ? column.render(booking[column.key as keyof Booking], booking)
                        : String(booking[column.key as keyof Booking] || '')
                      }
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {bookings.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No bookings match your current filters.
            </p>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

export default BookingListing;
